import Text from 'components/_UI/Text';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import { useCheckout } from 'context/checkout/Checkout';
import currency from 'helpers/currency';
import React, { useState, useEffect } from 'react';
import { Minus, MinusCircle, Plus, PlusCircle, ZoomIn } from 'lucide-react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import serviceFee from 'helpers/serviceFee';
import { trackEvent } from 'scripts/pixels';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import indexPriority from 'theme/z-index';

export default function ProductItem({
    product,
    minimal = false,
    loginModalCount = false,
    selectedProducts,
    setSelectedProducts,
    couponTrigger,
    onChangeTotalValue,
    activeOverlayProductId,
    onImageClick = () => false,
    onMouseEnter = () => false,
    onMouseLeave = () => false,
}) {
    const [quantity, setQuantity] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const batch = product.batches.find(batch => batch.is_active) || product.batches[0];

    const { checkout, setCheckout } = useCheckout();

    // Cartão e pix podem ter taxas diferentes. A vitrine exibe a de cartão e
    // carrega a de pix junto, para o total mudar junto com o meio escolhido.
    const feePerUnit = serviceFee.perUnit(batch, serviceFee.METHODS.CREDIT_CARD, product.absorbed_service_fee);
    const pixFeePerUnit = serviceFee.perUnit(batch, serviceFee.METHODS.PIX, product.absorbed_service_fee);

    const incrementQuantity = () => {
        if (quantity < product.quantity && quantity < 15) {
            setQuantity(prev => prev + 1);

            const updatedTotalPrice = batch.price * (quantity + 1);
            const updatedTotalOriginalPrice = Number(batch.original_price) * (quantity + 1);
            const priceChange = updatedTotalPrice - totalPrice;
            const totalPriceChange = updatedTotalOriginalPrice - (Number(batch.original_price) * quantity);

            setTotalPrice(updatedTotalPrice);
            onChangeTotalValue(priceChange, totalPriceChange, feePerUnit, pixFeePerUnit);

            // Adiciona uma nova instância do ticket completo ao selectedTickets
            setSelectedProducts(prev => [
                ...prev,
                product
            ]);

            // Uma unidade por clique: o valor do evento é o do produto somado,
            // não o carrinho inteiro, que já vai no InitiateCheckout.
            trackEvent('AddToCart', {
                value: batch.price,
                contentIds: [batch.id],
                numItems: 1,
            });
        }
    };

    const decrementQuantity = () => {
        if (quantity > 0) {
            setQuantity(prev => prev - 1);

            const updatedTotalPrice = batch.price * (quantity - 1);
            const updatedTotalOriginalPrice = Number(batch.original_price) * (quantity - 1);
            const priceChange = updatedTotalPrice - totalPrice;
            const totalPriceChange = updatedTotalOriginalPrice - (Number(batch.original_price) * quantity);

            setTotalPrice(updatedTotalPrice);
            onChangeTotalValue(priceChange, totalPriceChange, -feePerUnit, -pixFeePerUnit);

            // Remove apenas uma instância do produto correspondente
            setSelectedProducts(prev => {
                const productIndex = prev.findIndex(selectedProduct => selectedProduct.id === product.id);
                if (productIndex > -1) {
                    const updatedProducts = [...prev];
                    updatedProducts.splice(productIndex, 1); // Remove uma instância do produto
                    return updatedProducts;
                }
                return prev;
            });
        }
    };

    // Função para calcular a quantidade de produtos selecionados por id
    const calculateQuantityFromSelectedProducts = (id) => {
        return selectedProducts.filter(product => product.id === id).length;
    };

    useEffect(() => {
        if (loginModalCount) {
            setQuantity(0);
            setTotalPrice(0);
        }

        // Atualiza o checkout com base na quantidade selecionada e nos batches
        setCheckout(prevCheckout => {
            const existingBatches = prevCheckout.batches || [];

            // Remove os batches do produto atual do checkout
            const filteredBatches = existingBatches.filter(batch =>
                !product.batches.some(batchData => batchData.id === batch.id)
            );

            if (quantity === 0) {
                // Se a quantidade for 0, apenas retorne os batches filtrados
                return {
                    ...prevCheckout,
                    batches: filteredBatches
                };
            } else {
                // Adiciona ou atualiza os batches do produto atual
                const updatedBatches = product.batches.map(batchData => {
                    const existingBatch = filteredBatches.find(
                        batch => batch.id === batchData.id
                    );

                    if (existingBatch) {
                        // Atualiza a quantidade do lote existente
                        return { ...existingBatch, quantity: quantity };
                    } else {
                        // Adiciona o novo lote
                        return { id: batchData.id, quantity: quantity };
                    }
                });

                return {
                    ...prevCheckout,
                    batches: [...filteredBatches, ...updatedBatches]
                };
            }
        });
    }, [quantity]);

    useEffect(() => {
        // Atualiza a quantidade conforme o selectedTickets
        const newQuantity = calculateQuantityFromSelectedProducts(product.id);
        setQuantity(newQuantity);

        const updatedTotalPrice = batch.price * newQuantity;
        const updatedTotalOriginalPrice = Number(batch.original_price) * newQuantity;
        const priceChange = updatedTotalPrice - totalPrice;
        const totalPriceChange = updatedTotalOriginalPrice - totalPrice;
        const quantityChange = newQuantity - quantity;
        const feeChange = feePerUnit * quantityChange;
        const pixFeeChange = pixFeePerUnit * quantityChange;

        setTotalPrice(updatedTotalPrice);
        onChangeTotalValue(priceChange, totalPriceChange, feeChange, pixFeeChange);
    }, [couponTrigger, batch.price, batch.original_price])

    return (
        <ProductItemContainer minimal={minimal}>
            {/* min-width: 0 é o que deixa este flex item encolher abaixo do
                conteúdo (uma descrição comprida, por exemplo) em vez de
                estourar e empurrar o seletor de quantidade pra fora do card. */}
            <Row style={{ minWidth: 0 }}>
                {
                    product?.image ? (
                        <Column
                            style={{ position: 'relative' }}
                            height='100%'
                            width='auto'
                            onClick={() => onImageClick(product?.image)}
                            onMouseEnter={() => onMouseEnter && onMouseEnter(product?.image)}
                            onMouseLeave={() => onMouseLeave()}
                        >
                            <DynamicProfileImage
                                variant='squired'
                                size='60px'
                                img={product?.image}
                                word={product?.name}
                            />
                            {activeOverlayProductId === product.id &&
                                <ImageOverlay>
                                    <ZoomIn color={colors.neutral} size={'26px'} />
                                </ImageOverlay>
                            }
                        </Column>

                    ) : (
                        <img
                            src='/assets/images/placeholder-products.png'
                            style={{
                                width: 60,
                                height: 60,
                                borderRadius: 6
                            }}
                        />
                    )
                }


                <Column marginLeft='10px' style={{ minWidth: 0 }}>
                    <Text type='bold' paddingBottom={0} color={colors.nightDark}>
                        {product.name}
                    </Text>

                    {
                        product?.description && (
                            <Text marginTop={'8px'} name='small' paddingBottom={0} paddingRight={'25px'}>
                                {product?.description}
                            </Text>
                        )
                    }

                    {
                        // não tem desconto
                        Number(batch?.original_price) === Number(batch?.price) &&
                        <Row marginBottom={'5px'} verticalCenter>
                            <Text type='bold' color={colors.night} paddingBottom={0}>
                                {batch?.original_price != 0 ? currency.format(batch?.original_price) : 'Gratuito'}
                            </Text>
                            {
                                feePerUnit > 0 &&
                                <Text name='small' color={colors.night} marginLeft={'4px'} paddingBottom={0}>
                                    (+{feePerUnit.toFixed(2).replace('.', ',')} taxa)
                                </Text>
                            }
                        </Row>
                    }
                    {
                        // tem desconto
                        Number(batch?.original_price) !== Number(batch?.price) &&
                        <Row marginBottom={'8px'} verticalCenter>
                            <Text
                                color={colors.nightLight}
                                marginRight={'.5rem'}
                                decoration={'line-through'}
                            >
                                {currency.format(batch?.original_price)}
                            </Text>
                            <Text type='bold' color={colors.success} paddingBottom={0}>
                                {currency.format(batch?.price)}
                            </Text>
                            {
                                feePerUnit > 0 &&
                                <Text name='small' color={colors.night} marginLeft={'4px'} paddingBottom={0}>
                                    (+{feePerUnit.toFixed(2).replace('.', ',')} taxa)
                                </Text>
                            }
                        </Row>
                    }

                    {
                        product.quantity <= 15 &&
                        <LastUnits>
                            <Text
                                color={colors.occasionalOrange}
                                name='small'
                                paddingBottom={0}
                            >
                                Restam poucas unidades
                            </Text>
                        </LastUnits>

                    }
                </Column>
            </Row>

            <QuantityContainer>
                <ToggleValuesContainer fullyCentralized>
                    <ActionContainer minus disabled={quantity === 0}>
                        <Minus
                            color={quantity === 0 ? colors.night : colors.neutralLight}
                            size={20}
                            onClick={decrementQuantity}
                        />
                    </ActionContainer>
                    <Text marginLeft={'6px'} marginRight={'6px'} color={colors.night} paddingBottom={0}>
                        {quantity}
                    </Text>

                    <ActionContainer add disabled={quantity === 10}>
                        <Plus
                            color={quantity === 10 ? colors.nightLight : colors.neutralLight}
                            size={20}
                            onClick={incrementQuantity}
                        />
                    </ActionContainer>
                </ToggleValuesContainer>

                <Text type='bold' color={colors.night} paddingBottom={0}>
                    R$ {(totalPrice + feePerUnit * quantity).toFixed(2)}
                </Text>
            </QuantityContainer>

        </ProductItemContainer>
    )
}

const ProductItemContainer = styled(Row)`
    margin-top: 20px;
    /* padding: 14px 0; */
    align-items: center;
    /* background-color: ${colors.neutral}; */
    border-radius: 6px;
    margin-top: 14px;
    padding: 14px;
    border: 1px solid ${colors.neutralDark};
    box-shadow: 0 0 3px #ccc;
    /* border-bottom: 1px solid ${colors.nightLight}; */

    ${({ stoped }) => stoped && `
        opacity: .5;
    `};

    & > div > picture {
        cursor: pointer;
        position: relative;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        border-bottom: none;
        /* border-top: 1px solid ${colors.nightLight}; */
        /* margin-top: 0; */
        
        /* &:first-child {
            border-top: none;
        } */

        /* ${({ minimal }) => minimal && `
            border-top: none;
            border-bottom: 1px solid ${colors.nightLight};
        `} */
    }
`;

const LastUnits = styled(FullyCentered)`
    border: 1px solid ${colors.occasionalOrange};
    border-radius: 30px;
    width: 162px;
    padding: 5px;
    background-color: #fcf7eb;
`;

const QuantityContainer = styled(Column)`
    flex-direction: column;
    width: auto;
    align-items: center;
    width: 100px;
    /* Sem isto, uma descrição sem onde quebrar linha força o irmão a não
       encolher, e o flex empurra a sobra pra cá — os botões e o preço saem
       espremidos/deformados em vez de continuar com 100px. */
    flex-shrink: 0;
`

const ToggleValuesContainer = styled(Row)`
    margin-bottom: 10px;
    width: auto;
`;

const ActionContainer = styled(FullyCentered)`
    width: 30px;
    height: 30px;
    background: ${colors.occasionalPurple};
    border-radius: 6px;
    cursor: pointer;
    transition: all ease-in-out .25s;

    
    ${({ add }) => add && `
        margin-left: 4px;
        
        &:hover { 
            opacity: .8;
        }
    `}
    
    ${({ minus }) => minus && `
        margin-right: 4px;

        &:hover { 
            opacity: .8;
        }
    `}

    ${({ disabled }) => disabled && `
        background: ${colors.neutralLight};
        border: 1px solid ${colors.night};
        pointer-events: none;
    `}
`;

const ImageContainer = styled.div`
  overflow: hidden;
  border-radius: 6px;
  width: 60px;
  height: 60px;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.2);
    }
  }
`;

const ImageOverlay = styled(FullyCentered)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 60px;
    background: ${colors.nightDark};
    opacity: .84;
    border-radius: 6px;
    cursor: pointer;
    z-index: ${indexPriority.enormous};
    transition: ease-in-out all .35s;

`;